(() => {
  const STICKY_ATC_SELECTOR = '[data-sticky-atc]';
  const initializedBars = new WeakSet();

  function getBars(root = document) {
    if (root instanceof Element && root.matches(STICKY_ATC_SELECTOR)) {
      return [root];
    }

    return Array.from(root.querySelectorAll(STICKY_ATC_SELECTOR));
  }

  function parseJson(script) {
    if (!script) return null;

    try {
      return JSON.parse(script.textContent.trim());
    } catch (error) {
      console.warn('Sticky ATC: Variant JSON konnte nicht gelesen werden.', error);
      return null;
    }
  }

  function normalizeText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function getLabelByFor(root, id) {
    if (!root || !id) return null;

    if (window.CSS && typeof window.CSS.escape === 'function') {
      return root.querySelector(`label[for="${CSS.escape(id)}"]`);
    }

    return Array.from(root.querySelectorAll('label[for]')).find(
      (label) => label.getAttribute('for') === id
    );
  }

  function initStickyAtc(root = document) {
    getBars(root).forEach((bar) => {
      if (initializedBars.has(bar)) return;

      initializedBars.add(bar);

      const sectionId = bar.dataset.sectionId;
      const formId = bar.dataset.productFormId || `product-form-${sectionId}`;

      const variantData = parseJson(bar.querySelector('[data-sticky-atc-variants]')) || {};
      const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
      const variantsById = new Map(variants.map((variant) => [String(variant.id), variant]));

      const stickyButton = bar.querySelector('[data-sticky-atc-submit]');
      const stickyText = bar.querySelector('[data-sticky-atc-text]');
      const stickyPrice = bar.querySelector('[data-sticky-atc-price]');
      const stickyVariant = bar.querySelector('[data-sticky-atc-variant]');
      const stickyMedia = bar.querySelector('[data-sticky-atc-media]');
      const stickyImage = bar.querySelector('[data-sticky-atc-image]');
      const stickySpinner = bar.querySelector('.loading__spinner');

      const autoHideMobileQuery = window.matchMedia('(max-width: 749px)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      const AUTO_HIDE_UP_DISTANCE = 72;
      const AUTO_SHOW_DOWN_DISTANCE = 48;
      const MIN_SCROLL_DELTA = 4;

      const SCROLL_TO_PRODUCT_DURATION = 420;
      const SCROLL_TO_PRODUCT_OFFSET = 8;

      let originalAtcIsPassed = false;
      let hiddenBecauseUserScrollsUp = false;
      let lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      let upDistance = 0;
      let downDistance = 0;
      let scrollDirectionRafId = null;
      let isScrollingToProduct = false;

      const getProductInfo = () => document.getElementById(`MainProduct-${sectionId}`);
      const getForm = () => document.getElementById(formId);
      const getOriginalButton = () => document.getElementById(`ProductSubmitButton-${sectionId}`);
      const getOriginalButtonWrapper = () =>
        getOriginalButton()?.closest('.product-form__buttons') || getOriginalButton();

      const getPriceSource = () =>
        getProductInfo()?.querySelector(`#price-${sectionId}`) ||
        document.getElementById(`price-${sectionId}`);

      function resetScrollAutoHide() {
        hiddenBecauseUserScrollsUp = false;
        upDistance = 0;
        downDistance = 0;
        lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      }

      function applyStickyVisibility() {
        const shouldAutoHide = autoHideMobileQuery.matches;
        const isHiddenByScroll = shouldAutoHide && hiddenBecauseUserScrollsUp;
        const isVisible = originalAtcIsPassed && !isHiddenByScroll;

        bar.classList.toggle('is-visible', isVisible);
        bar.classList.toggle('is-scroll-hidden', originalAtcIsPassed && isHiddenByScroll);

        bar.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        document.documentElement.classList.toggle('sticky-atc-active', isVisible);
      }

      function setVisible(visible) {
        originalAtcIsPassed = visible;

        if (!visible || !autoHideMobileQuery.matches) {
          resetScrollAutoHide();
        }

        applyStickyVisibility();
      }

      function handleStickyScrollDirection() {
        scrollDirectionRafId = null;

        const currentScrollY = Math.max(
          window.scrollY || document.documentElement.scrollTop || 0,
          0
        );

        const delta = currentScrollY - lastScrollY;
        lastScrollY = currentScrollY;

        if (isScrollingToProduct) {
          if (autoHideMobileQuery.matches && originalAtcIsPassed) {
            hiddenBecauseUserScrollsUp = true;
            upDistance = AUTO_HIDE_UP_DISTANCE;
            downDistance = 0;
            applyStickyVisibility();
          }

          return;
        }

        if (!autoHideMobileQuery.matches || !originalAtcIsPassed) {
          if (hiddenBecauseUserScrollsUp || upDistance || downDistance) {
            resetScrollAutoHide();
            applyStickyVisibility();
          }

          return;
        }

        if (Math.abs(delta) < MIN_SCROLL_DELTA) return;

        const stickyIsLoading = stickyButton?.classList.contains('loading');
        const stickyHasFocus = bar.contains(document.activeElement);

        if (stickyIsLoading || stickyHasFocus) {
          resetScrollAutoHide();
          applyStickyVisibility();
          return;
        }

        if (delta < 0) {
          upDistance += Math.abs(delta);
          downDistance = 0;

          if (upDistance >= AUTO_HIDE_UP_DISTANCE && !hiddenBecauseUserScrollsUp) {
            hiddenBecauseUserScrollsUp = true;
            applyStickyVisibility();
          }
        }

        if (delta > 0) {
          downDistance += delta;
          upDistance = 0;

          if (downDistance >= AUTO_SHOW_DOWN_DISTANCE && hiddenBecauseUserScrollsUp) {
            hiddenBecauseUserScrollsUp = false;
            applyStickyVisibility();
          }
        }
      }

      function requestStickyScrollDirectionCheck() {
        if (scrollDirectionRafId) return;

        scrollDirectionRafId = window.requestAnimationFrame(handleStickyScrollDirection);
      }

      function handleStickyMediaChange() {
        resetScrollAutoHide();
        applyStickyVisibility();
      }

      function getStickyHeaderOffset() {
        const candidates = Array.from(
          document.querySelectorAll(
            'sticky-header, .shopify-section-header-sticky, .section-header, #shopify-section-header, header.header'
          )
        );

        return candidates.reduce((offset, element) => {
          const style = window.getComputedStyle(element);
          const rect = element.getBoundingClientRect();

          const isStickyOrFixed = style.position === 'sticky' || style.position === 'fixed';
          const isVisibleAtTop = rect.top <= 1 && rect.bottom > 0;

          if (!isStickyOrFixed || !isVisibleAtTop) return offset;

          return Math.max(offset, rect.height);
        }, 0);
      }

      function easeOutCubic(progress) {
        return 1 - Math.pow(1 - progress, 3);
      }

      function scrollToY(targetY, duration = SCROLL_TO_PRODUCT_DURATION, onComplete) {
        const startY = window.scrollY || document.documentElement.scrollTop || 0;
        const distance = targetY - startY;

        const complete = () => {
          if (typeof onComplete === 'function') {
            onComplete();
          }
        };

        if (reducedMotionQuery.matches || Math.abs(distance) < 2) {
          window.scrollTo(0, targetY);
          complete();
          return;
        }

        const startTime = window.performance.now();

        function tick(now) {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = easeOutCubic(progress);

          window.scrollTo(0, startY + distance * easedProgress);

          if (progress < 1) {
            window.requestAnimationFrame(tick);
          } else {
            complete();
          }
        }

        window.requestAnimationFrame(tick);
      }

      function getProductHeading() {
        const productInfo = getProductInfo();

        return (
          productInfo?.querySelector('h1') ||
          productInfo?.querySelector('.product__title h1') ||
          document.querySelector('main h1') ||
          document.querySelector('h1')
        );
      }

      function getProductScrollTargetY() {
        if (!autoHideMobileQuery.matches) {
          return 0;
        }

        const heading = getProductHeading();

        if (!heading) {
          return 0;
        }

        const headerOffset = getStickyHeaderOffset();

        return Math.max(
          0,
          heading.getBoundingClientRect().top +
            window.scrollY -
            headerOffset -
            SCROLL_TO_PRODUCT_OFFSET
        );
      }

      function handleScrollToProduct(event) {
        event.preventDefault();

        if (event.currentTarget && typeof event.currentTarget.blur === 'function') {
          event.currentTarget.blur();
        }

        if (autoHideMobileQuery.matches) {
          isScrollingToProduct = true;
          hiddenBecauseUserScrollsUp = true;
          upDistance = AUTO_HIDE_UP_DISTANCE;
          downDistance = 0;
          applyStickyVisibility();
        }

        scrollToY(getProductScrollTargetY(), SCROLL_TO_PRODUCT_DURATION, () => {
          isScrollingToProduct = false;
          lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
        });
      }

      window.addEventListener('scroll', requestStickyScrollDirectionCheck, { passive: true });

      if (typeof autoHideMobileQuery.addEventListener === 'function') {
        autoHideMobileQuery.addEventListener('change', handleStickyMediaChange);
      } else if (typeof autoHideMobileQuery.addListener === 'function') {
        autoHideMobileQuery.addListener(handleStickyMediaChange);
      }

      function getSelectedVariantId() {
        const variantInput = getForm()?.querySelector('input[name="id"], select[name="id"]');

        if (variantInput?.value) {
          return variantInput.value;
        }

        return new URLSearchParams(window.location.search).get('variant');
      }

      function getSelectedVariant() {
        const selectedVariantId = getSelectedVariantId();

        if (!selectedVariantId) return null;

        return variantsById.get(String(selectedVariantId)) || null;
      }

      function formatVariantText(variant) {
        if (!variant || variantData.hasOnlyDefaultVariant || variant.title === 'Default Title') {
          return '';
        }

        const optionNames = Array.isArray(variantData.optionNames) ? variantData.optionNames : [];

        return (variant.options || [])
          .map((value, index) => {
            if (!value || value === 'Default Title') return '';

            const optionName = optionNames[index];

            return optionName ? `${optionName}: ${value}` : value;
          })
          .filter(Boolean)
          .join(' · ');
      }

      function readVariantTextFromPicker() {
        const productInfo = getProductInfo();

        if (!productInfo) return '';

        const picker = productInfo.querySelector('variant-selects, variant-radios');

        if (!picker) return '';

        const parts = [];

        picker.querySelectorAll('fieldset').forEach((fieldset) => {
          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');

          if (!checkedInput) return;

          const legendText = normalizeText(fieldset.querySelector('legend')?.textContent);
          const optionName = normalizeText(legendText.split(':')[0]);
          const value = normalizeText(checkedInput.value);

          if (value) {
            parts.push(optionName ? `${optionName}: ${value}` : value);
          }
        });

        picker.querySelectorAll('select').forEach((select) => {
          if (select.name === 'id') return;

          const selectedOption = select.selectedOptions?.[0];
          const value = normalizeText(selectedOption?.textContent || select.value);

          const label =
            getLabelByFor(productInfo, select.id) ||
            select.closest('.product-form__input')?.querySelector('label');

          const optionName = normalizeText((label?.textContent || select.name).split(':')[0]);

          if (value) {
            parts.push(optionName ? `${optionName}: ${value}` : value);
          }
        });

        return Array.from(new Set(parts)).join(' · ');
      }

      function getCurrentGalleryImageData() {
        const gallery = document.getElementById(`MediaGallery-${sectionId}`);

        const galleryImage =
          gallery?.querySelector('.product__media-item.is-active img') ||
          gallery?.querySelector('.product__media-item[aria-current="true"] img') ||
          gallery?.querySelector('[data-media-id].is-active img') ||
          gallery?.querySelector('.product__media img') ||
          getProductInfo()?.querySelector('.product__media img');

        if (!galleryImage?.src) return null;

        return {
          src: galleryImage.currentSrc || galleryImage.src,
          srcset: galleryImage.srcset || '',
          alt: galleryImage.alt || '',
        };
      }

      function setStickyImage(imageData) {
        if (!stickyImage || !imageData?.src) {
          stickyMedia?.classList.add('hidden');
          return;
        }

        stickyImage.src = imageData.src;

        if (imageData.srcset) {
          stickyImage.srcset = imageData.srcset;
        } else {
          stickyImage.removeAttribute('srcset');
        }

        if (imageData.alt) {
          stickyImage.alt = imageData.alt;
        }

        stickyMedia?.classList.remove('hidden');
      }

      function syncVariant() {
        const variant = getSelectedVariant();

        const variantText = formatVariantText(variant) || readVariantTextFromPicker();

        if (stickyVariant) {
          stickyVariant.textContent = variantText;
          stickyVariant.classList.toggle('hidden', !variantText);
        }

        const imageData =
          variant?.image?.src
            ? variant.image
            : getCurrentGalleryImageData() || variantData.defaultImage;

        setStickyImage(imageData);
      }

      function syncButton() {
        const originalButton = getOriginalButton();

        if (!originalButton || !stickyButton) return;

        const originalText = originalButton.querySelector('span')?.textContent?.trim();

        if (originalText && stickyText) {
          stickyText.textContent = originalText;
        }

        const isDisabled =
          originalButton.hasAttribute('disabled') ||
          originalButton.getAttribute('aria-disabled') === 'true';

        const isLoading =
          originalButton.classList.contains('loading') ||
          originalButton.getAttribute('aria-disabled') === 'true';

        stickyButton.disabled = isDisabled;
        stickyButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        stickyButton.classList.toggle('loading', isLoading);

        if (stickySpinner) {
          stickySpinner.classList.toggle('hidden', !isLoading);
        }
      }

      function syncPrice() {
        const priceSource = getPriceSource();

        if (!priceSource || !stickyPrice) return;

        const sourcePrice = priceSource.querySelector('.price') || priceSource;

        stickyPrice.innerHTML = sourcePrice.outerHTML || sourcePrice.innerHTML;
        stickyPrice.classList.toggle('hidden', priceSource.classList.contains('hidden'));
      }

      function initVisibilityObserver() {
        const target = getOriginalButtonWrapper();

        if (!target) return;

        if (bar._stickyAtcIntersectionTarget === target) return;

        if (bar._stickyAtcIntersectionObserver) {
          bar._stickyAtcIntersectionObserver.disconnect();
        }

        bar._stickyAtcIntersectionTarget = target;

        const fallbackUpdate = () => {
          const rect = target.getBoundingClientRect();
          setVisible(rect.bottom < 0);
        };

        if ('IntersectionObserver' in window) {
          bar._stickyAtcIntersectionObserver = new IntersectionObserver(
            ([entry]) => {
              const originalButtonIsAboveViewport = entry.boundingClientRect.bottom < 0;
              setVisible(!entry.isIntersecting && originalButtonIsAboveViewport);
            },
            { threshold: 0 }
          );

          bar._stickyAtcIntersectionObserver.observe(target);
        } else {
          window.addEventListener('scroll', fallbackUpdate, { passive: true });
          window.addEventListener('resize', fallbackUpdate);
          fallbackUpdate();
        }
      }

      function syncAll() {
        syncButton();
        syncPrice();
        syncVariant();
        initVisibilityObserver();
      }

      let rafId = null;
      let scheduledTimeouts = [];

      function scheduleSync() {
        if (rafId) {
          window.cancelAnimationFrame(rafId);
        }

        scheduledTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
        scheduledTimeouts = [];

        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          syncAll();
        });

        scheduledTimeouts.push(window.setTimeout(syncAll, 150));
        scheduledTimeouts.push(window.setTimeout(syncAll, 450));
      }

      function observeProductInfo() {
        const productInfo = getProductInfo();

        if (!productInfo || bar._stickyAtcObservedNode === productInfo) return;

        if (bar._stickyAtcObserver) {
          bar._stickyAtcObserver.disconnect();
        }

        bar._stickyAtcObserver = new MutationObserver(() => {
          scheduleSync();
        });

        bar._stickyAtcObserver.observe(productInfo, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
          attributeFilter: [
            'disabled',
            'aria-disabled',
            'class',
            'hidden',
            'value',
            'src',
            'srcset',
          ],
        });

        bar._stickyAtcObservedNode = productInfo;
      }

      document.addEventListener('change', (event) => {
        const productInfo = getProductInfo();

        if (!productInfo || !productInfo.contains(event.target)) return;

        scheduleSync();
      });

      document.addEventListener('variant:change', scheduleSync);
      document.addEventListener('product:variant-change', scheduleSync);
      document.addEventListener('shopify:variant:change', scheduleSync);

      bar.querySelectorAll('[data-sticky-atc-scroll-to-product]').forEach((trigger) => {
        trigger.addEventListener('click', handleScrollToProduct);
      });

      stickyButton?.addEventListener('click', () => {
        const form = getForm();
        const originalButton = getOriginalButton();

        if (!form || !originalButton || stickyButton.disabled) return;

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(originalButton);
        } else {
          originalButton.click();
        }
      });

      bar._stickyAtcRefresh = () => {
        observeProductInfo();
        syncAll();
      };

      bar._stickyAtcRefresh();
    });
  }

  function refreshStickyAtc() {
    document.querySelectorAll(STICKY_ATC_SELECTOR).forEach((bar) => {
      bar._stickyAtcRefresh?.();
    });
  }

  function boot(root = document) {
    initStickyAtc(root);
    refreshStickyAtc();
  }

  document.addEventListener('DOMContentLoaded', () => boot(document));

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });

  document.addEventListener('product-info:loaded', () => {
    boot(document);
  });
})();